import type { Lang } from '../i18n'

/**
 * Long-form legal copy lives here, not in the i18n dicts — the dicts hold UI
 * strings and are parity-tested key-by-key; these are documents. Each language
 * carries the full text; Danish/German note that English prevails.
 *
 * Grounded in what the system actually does (see Privacy §2–§7). If behaviour
 * changes — new processor, new data category, self-serve deletion — update the
 * relevant section AND the `updated` date.
 */

export interface LegalSection {
  h: string
  ps: string[]
}
export interface LegalDoc {
  title: string
  updated: string // shown as-is
  intro: string[]
  sections: LegalSection[]
}

const COMPANY = 'SG Birch Services Limited'
const CONTACT = 'privacy@horsesheli.com'
const SUPPORT = 'support@horsesheli.com'

// ─────────────────────────────────────────────────────────────────── PRIVACY ──

const privacyEn: LegalDoc = {
  title: 'Privacy Policy',
  updated: '15 July 2026',
  intro: [
    `HorseSheli (horsesheli.com) is operated by ${COMPANY}, a company registered in England and Wales ("we", "us"). We are the data controller for the personal data described here. Contact: ${CONTACT}.`,
    'The short version: we store what you put into HorseSheli so the service works, we don’t run ads or trackers, we don’t sell data, and what your yard can see is controlled by you.',
  ],
  sections: [
    {
      h: '1. What we store',
      ps: [
        'Account: your email address, display name and language preference.',
        'Horses: the profiles you create — including photos, identification details you choose to record (microchip and passport numbers), medical records, documents you upload, and care reminders.',
        'Yards: yard details (name, address, contact and emergency numbers), membership and roles (owner, manager, groom, livery), daily tasks with who ticked them, and livery invoices issued through the app.',
        'Billing: subscriptions are processed by Stripe. We store your subscription status and Stripe reference IDs — never card numbers.',
        'Technical: standard server logs (IP address, timestamps) used for security, rate-limiting and abuse prevention.',
      ],
    },
    {
      h: '2. Why we use it',
      ps: [
        'To provide the service you signed up for — storing and showing your records, sending the emails you trigger (yard invitations) or set up (care reminders) — on the basis of our contract with you.',
        'To take payment for subscriptions (contract and legal obligations).',
        'To keep the platform secure and stop abuse — rate limits, bot protection at sign-up, image moderation on uploads (legitimate interest).',
        'We do not send marketing email and we do not profile you.',
      ],
    },
    {
      h: '3. Who sees what inside HorseSheli',
      ps: [
        'Your horses are private by default. If you place a horse at a yard, that yard sees its care data — and you control the categories (medical book, documents, reminders, details) with per-horse sharing toggles.',
        'Yard staff see the yard’s member list. Contact emails are visible to staff only; fellow liveries see names, roles and horse counts.',
        'Making a horse’s profile public is your explicit choice and can be reversed at any time.',
      ],
    },
    {
      h: '4. Service providers',
      ps: [
        'Amazon Web Services — hosting and email delivery. Data is stored in the EU (Ireland, eu-west-1).',
        'Stripe — payment processing.',
        'Cloudflare — bot protection on the sign-up form (Turnstile).',
        'These providers process data on our instructions. We do not sell or share personal data for advertising. No analytics trackers run on the site.',
      ],
    },
    {
      h: '5. Cookies and local storage',
      ps: [
        'We use only what is essential: your login session and language choice are kept in your browser’s local storage. There are no tracking cookies and no third-party advertising or analytics — which is why you don’t see a cookie banner.',
      ],
    },
    {
      h: '6. How long we keep things',
      ps: [
        'Your data stays as long as your account is active. Content you delete (photos, documents, records) is removed from the live system promptly.',
        'Emails sent to our privacy@ and support@ addresses pass through transit storage that is automatically emptied after 30 days.',
        'Encrypted database backups are retained for up to 35 days for disaster recovery.',
      ],
    },
    {
      h: '7. Your rights',
      ps: [
        'Under UK and EU data protection law you can ask for access to your data, correction, deletion, restriction, portability, or object to processing.',
        `Email ${CONTACT} and we will respond within one month. Account deletion is currently handled by email — write to us and we will delete your account and its data.`,
        'You can also complain to the Information Commissioner’s Office (ICO) in the UK or your local supervisory authority.',
      ],
    },
    {
      h: '8. Where data lives',
      ps: [
        'HorseSheli runs in the EU (Ireland). Stripe and Cloudflare may process limited data outside the UK/EEA; where they do, transfers are covered by adequacy decisions or standard contractual clauses.',
      ],
    },
    {
      h: '9. Children',
      ps: ['HorseSheli is not directed at children under 16. If you believe a child has created an account without parental consent, contact us and we will remove it.'],
    },
    {
      h: '10. Changes',
      ps: [`If this policy changes materially we will post the new version here with an updated date. Questions any time: ${CONTACT}.`],
    },
  ],
}

const privacyDa: LegalDoc = {
  title: 'Privatlivspolitik',
  updated: '15. juli 2026',
  intro: [
    `HorseSheli (horsesheli.com) drives af ${COMPANY}, et selskab registreret i England og Wales ("vi", "os"). Vi er dataansvarlig for de personoplysninger, der beskrives her. Kontakt: ${CONTACT}. Denne oversættelse er en service — den engelske version gælder ved uoverensstemmelser.`,
    'Den korte version: Vi gemmer det, du lægger ind i HorseSheli, så tjenesten virker. Vi kører ingen reklamer eller trackere, vi sælger ikke data, og du styrer selv, hvad din stald kan se.',
  ],
  sections: [
    {
      h: '1. Hvad vi gemmer',
      ps: [
        'Konto: din e-mailadresse, dit visningsnavn og dit sprogvalg.',
        'Heste: de profiler, du opretter — herunder fotos, identifikation du vælger at registrere (chip- og pasnumre), sundhedsjournaler, dokumenter du uploader, og påmindelser.',
        'Stalde: staldens oplysninger (navn, adresse, kontakt- og nødnumre), medlemskaber og roller (ejer, manager, staldpersonale, pensionær), daglige opgaver med hvem der har sat flueben, og fakturaer udstedt gennem appen.',
        'Betaling: abonnementer behandles af Stripe. Vi gemmer din abonnementsstatus og Stripe-reference-id’er — aldrig kortnumre.',
        'Teknisk: almindelige serverlogfiler (IP-adresse, tidsstempler) til sikkerhed, hastighedsbegrænsning og misbrugsforebyggelse.',
      ],
    },
    {
      h: '2. Hvorfor vi bruger det',
      ps: [
        'For at levere tjenesten — gemme og vise dine data og sende de e-mails, du selv udløser (staldinvitationer) eller opsætter (påmindelser) — på grundlag af vores aftale med dig.',
        'For at opkræve betaling for abonnementer (aftale og retlige forpligtelser).',
        'For at holde platformen sikker og stoppe misbrug — hastighedsgrænser, botbeskyttelse ved tilmelding, billedmoderation ved upload (legitim interesse).',
        'Vi sender ikke markedsføringsmails, og vi profilerer dig ikke.',
      ],
    },
    {
      h: '3. Hvem ser hvad i HorseSheli',
      ps: [
        'Dine heste er private som standard. Opstalder du en hest på en stald, ser stalden dens pasningsdata — og du styrer kategorierne (sundhedsjournal, dokumenter, påmindelser, detaljer) med delingsknapper pr. hest.',
        'Staldens personale ser medlemslisten. Kontakt-e-mails er kun synlige for personale; andre pensionærer ser navne, roller og antal heste.',
        'At gøre en hesteprofil offentlig er dit eget aktive valg og kan altid fortrydes.',
      ],
    },
    {
      h: '4. Databehandlere',
      ps: [
        'Amazon Web Services — hosting og e-maillevering. Data opbevares i EU (Irland, eu-west-1).',
        'Stripe — betalingsbehandling.',
        'Cloudflare — botbeskyttelse på tilmeldingsformularen (Turnstile).',
        'Disse leverandører behandler data efter vores instruks. Vi sælger eller deler ikke personoplysninger til reklameformål. Der kører ingen analysetrackere på sitet.',
      ],
    },
    {
      h: '5. Cookies og lokal lagring',
      ps: [
        'Vi bruger kun det nødvendige: din login-session og dit sprogvalg ligger i browserens lokale lager. Der er ingen tracking-cookies og ingen tredjepartsreklamer eller -analyse — derfor ser du intet cookiebanner.',
      ],
    },
    {
      h: '6. Hvor længe vi gemmer',
      ps: [
        'Dine data bliver, så længe din konto er aktiv. Indhold, du sletter (fotos, dokumenter, journaler), fjernes hurtigt fra det aktive system.',
        'E-mails til vores privacy@- og support@-adresser passerer et transitlager, der tømmes automatisk efter 30 dage.',
        'Krypterede database-backups opbevares i op til 35 dage til katastrofegendannelse.',
      ],
    },
    {
      h: '7. Dine rettigheder',
      ps: [
        'Efter britisk og europæisk databeskyttelsesret kan du bede om indsigt, berigtigelse, sletning, begrænsning, dataportabilitet eller gøre indsigelse mod behandling.',
        `Skriv til ${CONTACT} — vi svarer inden for en måned. Kontosletning håndteres i øjeblikket pr. e-mail: skriv til os, så sletter vi din konto og dens data.`,
        'Du kan også klage til Datatilsynet eller til den britiske ICO.',
      ],
    },
    {
      h: '8. Hvor data ligger',
      ps: [
        'HorseSheli kører i EU (Irland). Stripe og Cloudflare kan behandle begrænsede data uden for UK/EØS; hvor det sker, er overførsler dækket af tilstrækkelighedsafgørelser eller standardkontraktbestemmelser.',
      ],
    },
    {
      h: '9. Børn',
      ps: ['HorseSheli henvender sig ikke til børn under 16 år. Mener du, at et barn har oprettet en konto uden forældresamtykke, så kontakt os, og vi fjerner den.'],
    },
    {
      h: '10. Ændringer',
      ps: [`Ændres denne politik væsentligt, offentliggør vi den nye version her med opdateret dato. Spørgsmål: ${CONTACT}.`],
    },
  ],
}

const privacyDe: LegalDoc = {
  title: 'Datenschutzerklärung',
  updated: '15. Juli 2026',
  intro: [
    `HorseSheli (horsesheli.com) wird von ${COMPANY} betrieben, einer in England und Wales registrierten Gesellschaft („wir“, „uns“). Wir sind Verantwortlicher für die hier beschriebenen personenbezogenen Daten. Kontakt: ${CONTACT}. Diese Übersetzung ist ein Service — bei Abweichungen gilt die englische Fassung.`,
    'Kurzfassung: Wir speichern, was du in HorseSheli einträgst, damit der Dienst funktioniert. Keine Werbung, keine Tracker, kein Datenverkauf — und was dein Stall sieht, bestimmst du.',
  ],
  sections: [
    {
      h: '1. Was wir speichern',
      ps: [
        'Konto: deine E-Mail-Adresse, dein Anzeigename und deine Spracheinstellung.',
        'Pferde: die von dir angelegten Profile — einschließlich Fotos, von dir erfasster Identifikation (Chip- und Passnummern), Krankenakte, hochgeladener Dokumente und Erinnerungen.',
        'Ställe: Stalldaten (Name, Adresse, Kontakt- und Notfallnummern), Mitgliedschaften und Rollen (Inhaber, Manager, Pfleger, Einsteller), tägliche Aufgaben samt Abhak-Vermerk und über die App gestellte Rechnungen.',
        'Zahlung: Abonnements laufen über Stripe. Wir speichern deinen Abo-Status und Stripe-Referenz-IDs — niemals Kartennummern.',
        'Technisch: übliche Server-Logs (IP-Adresse, Zeitstempel) für Sicherheit, Ratenbegrenzung und Missbrauchsabwehr.',
      ],
    },
    {
      h: '2. Wofür wir es nutzen',
      ps: [
        'Zur Bereitstellung des Dienstes — Speichern und Anzeigen deiner Daten, Versand der E-Mails, die du auslöst (Stall-Einladungen) oder einrichtest (Erinnerungen) — auf Grundlage unseres Vertrags mit dir.',
        'Zur Abwicklung von Abonnementzahlungen (Vertrag und rechtliche Pflichten).',
        'Für Sicherheit und Missbrauchsabwehr — Ratenlimits, Bot-Schutz bei der Registrierung, Bildmoderation bei Uploads (berechtigtes Interesse).',
        'Wir versenden keine Marketing-E-Mails und erstellen keine Profile.',
      ],
    },
    {
      h: '3. Wer was in HorseSheli sieht',
      ps: [
        'Deine Pferde sind standardmäßig privat. Stellst du ein Pferd in einen Stall ein, sieht dieser Stall die Pflegedaten — die Kategorien (Krankenakte, Dokumente, Erinnerungen, Details) steuerst du pro Pferd über Freigabe-Schalter.',
        'Das Stallpersonal sieht die Mitgliederliste. Kontakt-E-Mails sehen nur Mitarbeitende; andere Einsteller sehen Namen, Rollen und Pferdeanzahl.',
        'Ein Pferdeprofil öffentlich zu machen ist deine ausdrückliche Entscheidung und jederzeit umkehrbar.',
      ],
    },
    {
      h: '4. Auftragsverarbeiter',
      ps: [
        'Amazon Web Services — Hosting und E-Mail-Versand. Daten liegen in der EU (Irland, eu-west-1).',
        'Stripe — Zahlungsabwicklung.',
        'Cloudflare — Bot-Schutz im Registrierungsformular (Turnstile).',
        'Diese Anbieter verarbeiten Daten nach unserer Weisung. Wir verkaufen oder teilen keine personenbezogenen Daten zu Werbezwecken. Auf der Seite laufen keine Analyse-Tracker.',
      ],
    },
    {
      h: '5. Cookies und lokale Speicherung',
      ps: [
        'Wir nutzen nur das Nötigste: Login-Sitzung und Sprachwahl liegen im lokalen Speicher deines Browsers. Keine Tracking-Cookies, keine Dritt-Werbung oder -Analyse — deshalb gibt es auch kein Cookie-Banner.',
      ],
    },
    {
      h: '6. Speicherdauer',
      ps: [
        'Deine Daten bleiben, solange dein Konto aktiv ist. Gelöschte Inhalte (Fotos, Dokumente, Einträge) werden zeitnah aus dem Live-System entfernt.',
        'E-Mails an unsere privacy@- und support@-Adressen durchlaufen einen Transitspeicher, der nach 30 Tagen automatisch geleert wird.',
        'Verschlüsselte Datenbank-Backups werden bis zu 35 Tage für die Notfallwiederherstellung aufbewahrt.',
      ],
    },
    {
      h: '7. Deine Rechte',
      ps: [
        'Nach britischem und EU-Datenschutzrecht kannst du Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit oder Widerspruch verlangen.',
        `Schreib an ${CONTACT} — wir antworten innerhalb eines Monats. Die Kontolöschung läuft derzeit per E-Mail: Schreib uns, und wir löschen dein Konto samt Daten.`,
        'Du kannst dich außerdem bei deiner Datenschutz-Aufsichtsbehörde oder der britischen ICO beschweren.',
      ],
    },
    {
      h: '8. Wo die Daten liegen',
      ps: [
        'HorseSheli läuft in der EU (Irland). Stripe und Cloudflare können begrenzte Daten außerhalb des UK/EWR verarbeiten; solche Übermittlungen sind durch Angemessenheitsbeschlüsse oder Standardvertragsklauseln abgesichert.',
      ],
    },
    {
      h: '9. Kinder',
      ps: ['HorseSheli richtet sich nicht an Kinder unter 16 Jahren. Wenn du glaubst, dass ein Kind ohne elterliche Zustimmung ein Konto angelegt hat, kontaktiere uns — wir entfernen es.'],
    },
    {
      h: '10. Änderungen',
      ps: [`Bei wesentlichen Änderungen veröffentlichen wir die neue Fassung hier mit aktualisiertem Datum. Fragen jederzeit an ${CONTACT}.`],
    },
  ],
}

// ───────────────────────────────────────────────────────────────────── TERMS ──

const termsEn: LegalDoc = {
  title: 'Terms of Service',
  updated: '15 July 2026',
  intro: [
    `These terms are a contract between you and ${COMPANY}, registered in England and Wales, for the use of HorseSheli (horsesheli.com). Support: ${SUPPORT}.`,
  ],
  sections: [
    {
      h: '1. The service',
      ps: [
        'HorseSheli is a record-keeping and yard-management tool for horse owners and livery yards: horse profiles, medical books, documents, reminders, yard rosters, daily tasks, member roles and livery invoicing.',
        'It supports your record-keeping — it is not veterinary advice, and reminders are aids, not guarantees. Care decisions stay with you and your vet.',
      ],
    },
    {
      h: '2. Accounts',
      ps: [
        'Keep your sign-in details safe and your account information accurate. One account per person. You must be 16 or older, or have parental consent.',
      ],
    },
    {
      h: '3. Plans, trials and billing',
      ps: [
        'The horse-owner base tier is free (currently up to 3 horses with a storage allowance). Paid plans: the yard subscription (per yard, monthly) and the horse-owner premium (monthly) — current prices are always shown in the app before you pay.',
        'Yards get a free trial before payment starts. Payments are processed by Stripe. Subscriptions renew automatically; cancel any time and the plan runs to the end of the paid period. We may change prices with reasonable advance notice — changes apply from your next renewal.',
      ],
    },
    {
      h: '4. Your content',
      ps: [
        'What you upload stays yours. You grant us the licence needed to host, process and display it — that is, to run the service.',
        'You are responsible for what you upload and for having the right to upload it. No unlawful, infringing or abusive content. Images pass automated moderation and may be rejected.',
      ],
    },
    {
      h: '5. Yards, roles and invoices between users',
      ps: [
        'Yard owners and managers control their yard’s records (tasks, invoices, member roles). Horse owners control their horses’ data and what the yard can see.',
        'Livery invoices issued through HorseSheli are between the yard and its client. We provide the tooling; we are not a party to that relationship and do not collect or hold those payments.',
      ],
    },
    {
      h: '6. Acceptable use',
      ps: [
        'No abuse of other users, no attempts to break, probe or overload the platform, no scraping, no spam, no circumventing limits or access controls. Rate limits are enforced automatically.',
      ],
    },
    {
      h: '7. Availability and your copies',
      ps: [
        'The service is provided “as is”. We aim for high availability but do not guarantee uninterrupted service. Keep your own copies of documents that matter (passports, registrations, insurance).',
      ],
    },
    {
      h: '8. Liability',
      ps: [
        'Nothing in these terms excludes liability that cannot lawfully be excluded (for example for fraud, or death or personal injury caused by negligence).',
        'Otherwise, our total liability for claims connected to the service is limited to the fees you paid us in the 12 months before the claim, and we are not liable for indirect losses (lost profits, lost data that you could reasonably have backed up, business interruption).',
      ],
    },
    {
      h: '9. Ending things',
      ps: [
        `You can stop using HorseSheli at any time; email ${SUPPORT} to delete your account and data. We may suspend or terminate accounts that break these terms, abuse the platform, or don’t pay for an active subscription — with notice where reasonably possible.`,
      ],
    },
    {
      h: '10. Changes to these terms',
      ps: [
        'We may update these terms; material changes will be announced in the app or by email with reasonable notice. Using the service after a change takes effect means you accept it.',
      ],
    },
    {
      h: '11. Law',
      ps: [
        'These terms are governed by the law of England and Wales, and its courts have jurisdiction — but if you are a consumer elsewhere, you keep the protections and forum your local law grants you.',
      ],
    },
  ],
}

const termsDa: LegalDoc = {
  title: 'Vilkår for brug',
  updated: '15. juli 2026',
  intro: [
    `Disse vilkår er en aftale mellem dig og ${COMPANY}, registreret i England og Wales, om brugen af HorseSheli (horsesheli.com). Support: ${SUPPORT}. Denne oversættelse er en service — den engelske version gælder ved uoverensstemmelser.`,
  ],
  sections: [
    {
      h: '1. Tjenesten',
      ps: [
        'HorseSheli er et journal- og staldstyringsværktøj for hesteejere og pensionsstalde: hesteprofiler, sundhedsjournaler, dokumenter, påmindelser, staldoversigter, daglige opgaver, medlemsroller og fakturering.',
        'Det understøtter din journalføring — det er ikke veterinærrådgivning, og påmindelser er hjælpemidler, ikke garantier. Beslutninger om pasning ligger hos dig og din dyrlæge.',
      ],
    },
    {
      h: '2. Konti',
      ps: ['Pas godt på dine loginoplysninger, og hold dine kontooplysninger korrekte. Én konto pr. person. Du skal være mindst 16 år eller have forældresamtykke.'],
    },
    {
      h: '3. Abonnementer, prøveperioder og betaling',
      ps: [
        'Basisniveauet for hesteejere er gratis (i øjeblikket op til 3 heste med en lagerkvote). Betalte abonnementer: staldabonnementet (pr. stald, månedligt) og premium for hesteejere (månedligt) — aktuelle priser vises altid i appen, før du betaler.',
        'Stalde får en gratis prøveperiode, før betaling starter. Betalinger behandles af Stripe. Abonnementer fornys automatisk; opsig når som helst — abonnementet løber til udgangen af den betalte periode. Priser kan ændres med rimeligt varsel og gælder fra næste fornyelse.',
      ],
    },
    {
      h: '4. Dit indhold',
      ps: [
        'Det, du uploader, er dit. Du giver os den licens, der er nødvendig for at hoste, behandle og vise det — altså for at drive tjenesten.',
        'Du er ansvarlig for det, du uploader, og for at have retten til det. Intet ulovligt, krænkende eller stødende indhold. Billeder gennemgår automatisk moderation og kan blive afvist.',
      ],
    },
    {
      h: '5. Stalde, roller og fakturaer mellem brugere',
      ps: [
        'Staldejere og managere styrer staldens data (opgaver, fakturaer, medlemsroller). Hesteejere styrer deres hestes data, og hvad stalden kan se.',
        'Fakturaer udstedt gennem HorseSheli er et anliggende mellem stalden og dens kunde. Vi leverer værktøjet; vi er ikke part i forholdet og modtager eller opbevarer ikke disse betalinger.',
      ],
    },
    {
      h: '6. Acceptabel brug',
      ps: ['Ingen chikane af andre brugere, ingen forsøg på at bryde, afsøge eller overbelaste platformen, ingen scraping, ingen spam, ingen omgåelse af grænser eller adgangskontrol. Hastighedsgrænser håndhæves automatisk.'],
    },
    {
      h: '7. Tilgængelighed og dine kopier',
      ps: ['Tjenesten leveres, som den er. Vi tilstræber høj oppetid men garanterer ikke uafbrudt drift. Behold egne kopier af vigtige dokumenter (pas, registreringer, forsikringer).'],
    },
    {
      h: '8. Ansvar',
      ps: [
        'Intet i disse vilkår udelukker ansvar, der ikke lovligt kan udelukkes (fx svig eller død og personskade forvoldt ved uagtsomhed).',
        'Derudover er vores samlede ansvar for krav i forbindelse med tjenesten begrænset til de beløb, du har betalt os de seneste 12 måneder før kravet, og vi hæfter ikke for indirekte tab (tabt fortjeneste, tabte data du med rimelighed kunne have sikkerhedskopieret, driftstab).',
      ],
    },
    {
      h: '9. Ophør',
      ps: [
        `Du kan stoppe med at bruge HorseSheli når som helst; skriv til ${SUPPORT} for at få slettet din konto og dine data. Vi kan suspendere eller lukke konti, der bryder vilkårene, misbruger platformen eller ikke betaler for et aktivt abonnement — med varsel, hvor det med rimelighed er muligt.`,
      ],
    },
    {
      h: '10. Ændringer af vilkårene',
      ps: ['Vi kan opdatere vilkårene; væsentlige ændringer varsles i appen eller pr. e-mail i rimelig tid. Fortsat brug efter ikrafttræden betyder accept.'],
    },
    {
      h: '11. Lovvalg',
      ps: ['Vilkårene er underlagt engelsk og walisisk ret med værneting dér — men er du forbruger i et andet land, beholder du de beskyttelser og det værneting, din lokale lovgivning giver dig.'],
    },
  ],
}

const termsDe: LegalDoc = {
  title: 'Nutzungsbedingungen',
  updated: '15. Juli 2026',
  intro: [
    `Diese Bedingungen sind ein Vertrag zwischen dir und ${COMPANY}, registriert in England und Wales, über die Nutzung von HorseSheli (horsesheli.com). Support: ${SUPPORT}. Diese Übersetzung ist ein Service — bei Abweichungen gilt die englische Fassung.`,
  ],
  sections: [
    {
      h: '1. Der Dienst',
      ps: [
        'HorseSheli ist ein Dokumentations- und Stallverwaltungswerkzeug für Pferdebesitzer und Pensionsställe: Pferdeprofile, Krankenakten, Dokumente, Erinnerungen, Stallübersichten, tägliche Aufgaben, Mitgliederrollen und Rechnungsstellung.',
        'Es unterstützt deine Dokumentation — es ist keine tierärztliche Beratung, und Erinnerungen sind Hilfen, keine Garantien. Entscheidungen über die Pflege liegen bei dir und deinem Tierarzt.',
      ],
    },
    {
      h: '2. Konten',
      ps: ['Schütze deine Zugangsdaten und halte deine Kontodaten aktuell. Ein Konto pro Person. Du musst mindestens 16 Jahre alt sein oder die Zustimmung deiner Eltern haben.'],
    },
    {
      h: '3. Tarife, Testphase und Zahlung',
      ps: [
        'Die Basisstufe für Pferdebesitzer ist kostenlos (derzeit bis zu 3 Pferde mit Speicherkontingent). Bezahlte Tarife: das Stall-Abo (pro Stall, monatlich) und Premium für Pferdebesitzer (monatlich) — die aktuellen Preise werden dir vor jeder Zahlung in der App angezeigt.',
        'Ställe erhalten eine kostenlose Testphase, bevor die Zahlung beginnt. Zahlungen laufen über Stripe. Abos verlängern sich automatisch; du kannst jederzeit kündigen — der Tarif läuft bis zum Ende des bezahlten Zeitraums. Preisänderungen kündigen wir mit angemessener Frist an; sie gelten ab der nächsten Verlängerung.',
      ],
    },
    {
      h: '4. Deine Inhalte',
      ps: [
        'Was du hochlädst, bleibt deins. Du räumst uns die Lizenz ein, die zum Hosten, Verarbeiten und Anzeigen nötig ist — also zum Betrieb des Dienstes.',
        'Du bist verantwortlich für deine Uploads und dafür, die Rechte daran zu haben. Keine rechtswidrigen, rechtsverletzenden oder missbräuchlichen Inhalte. Bilder durchlaufen eine automatische Moderation und können abgelehnt werden.',
      ],
    },
    {
      h: '5. Ställe, Rollen und Rechnungen zwischen Nutzern',
      ps: [
        'Stallinhaber und Manager verwalten die Daten ihres Stalls (Aufgaben, Rechnungen, Mitgliederrollen). Pferdebesitzer steuern die Daten ihrer Pferde und was der Stall sehen kann.',
        'Über HorseSheli gestellte Rechnungen betreffen allein den Stall und seinen Kunden. Wir stellen das Werkzeug; wir sind nicht Vertragspartei und ziehen diese Zahlungen weder ein noch verwahren wir sie.',
      ],
    },
    {
      h: '6. Zulässige Nutzung',
      ps: ['Kein Missbrauch gegenüber anderen Nutzern, keine Versuche, die Plattform zu manipulieren, auszuforschen oder zu überlasten, kein Scraping, kein Spam, kein Umgehen von Limits oder Zugriffskontrollen. Ratenlimits werden automatisch durchgesetzt.'],
    },
    {
      h: '7. Verfügbarkeit und eigene Kopien',
      ps: ['Der Dienst wird bereitgestellt, „wie er ist“. Wir streben hohe Verfügbarkeit an, garantieren aber keinen unterbrechungsfreien Betrieb. Bewahre eigene Kopien wichtiger Dokumente auf (Pässe, Registrierungen, Versicherungen).'],
    },
    {
      h: '8. Haftung',
      ps: [
        'Nichts in diesen Bedingungen schließt Haftung aus, die gesetzlich nicht ausgeschlossen werden kann (z. B. für Vorsatz oder für Schäden an Leben, Körper und Gesundheit durch Fahrlässigkeit).',
        'Im Übrigen ist unsere Gesamthaftung für Ansprüche im Zusammenhang mit dem Dienst auf die Beträge begrenzt, die du uns in den 12 Monaten vor dem Anspruch gezahlt hast; für indirekte Schäden (entgangener Gewinn, verlorene Daten, die du zumutbar hättest sichern können, Betriebsunterbrechung) haften wir nicht.',
      ],
    },
    {
      h: '9. Beendigung',
      ps: [
        `Du kannst HorseSheli jederzeit verlassen; schreib an ${SUPPORT}, um dein Konto und deine Daten löschen zu lassen. Wir können Konten sperren oder kündigen, die gegen diese Bedingungen verstoßen, die Plattform missbrauchen oder ein aktives Abo nicht bezahlen — mit Ankündigung, wo dies zumutbar ist.`,
      ],
    },
    {
      h: '10. Änderungen dieser Bedingungen',
      ps: ['Wir können diese Bedingungen aktualisieren; wesentliche Änderungen kündigen wir in der App oder per E-Mail mit angemessener Frist an. Die weitere Nutzung nach Inkrafttreten gilt als Zustimmung.'],
    },
    {
      h: '11. Recht',
      ps: ['Es gilt das Recht von England und Wales mit Gerichtsstand dort — als Verbraucher in einem anderen Land behältst du jedoch den Schutz und den Gerichtsstand, den dir dein lokales Recht gewährt.'],
    },
  ],
}

export const LEGAL: Record<'privacy' | 'terms', Record<Lang, LegalDoc>> = {
  privacy: { en: privacyEn, da: privacyDa, de: privacyDe },
  terms: { en: termsEn, da: termsDa, de: termsDe },
}
